import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import onboardingService from "../services/onboardingService";

const OnboardingContext = createContext();

// ── Employee onboarding flow (4 steps) ───────────────────────────────────────
const EMPLOYEE_STEPS = [
  {
    id: "connectAccounts",
    title: "Connect Your Personal Social Handles",
    description: "Link your LinkedIn and X profiles under Settings so you can share content directly to your personal networks.",
    target: "#onboarding-personal-accounts-accordion",
    route: "/employees/settings",
    action: "expand-personal-accounts",
    placement: "right",
  },
  {
    id: "personalTrendingTopics",
    title: "Update Your Personal Trending Topics",
    description: "Set your personal trending topics to keep your feed relevant and tailored to your interests.",
    target: "#onboarding-personal-trending-topics-accordion",
    route: "/employees/settings",
    action: "expand-personal-trending-topics",
    placement: "right",
  },
  {
    id: "sharePost",
    title: "Share Content",
    description: "Use 'Share with Thoughts' to add your perspective or 'Quick Share' to instantly post to your connected networks.",
    target: "#onboarding-share-post-btn",
    route: "/employees/timeline",
    placement: "bottom",
  },
  {
    id: "createContent",
    title: "Create Content",
    description: "Head to the Compose page to write original posts, use AI generation, attach media, and schedule your own content.",
    target: "#onboarding-compose-page-input",
    route: "/employees/compose",
    placement: "bottom",
  },
];

// ── Admin onboarding flow (8 steps) ──────────────────────────────────────────
const ADMIN_STEPS = [
  {
    id: "linkSocialPages",
    title: "Make Sure Your Social Pages Are Linked",
    description: "Connect your company LinkedIn and X pages under Settings so your team can publish content directly to your brand channels.",
    target: "#onboarding-business-pages-accordion",
    route: "/admin/settings",
    action: "expand-business-pages",
    placement: "right",
  },
  {
    id: "connectPersonalAccounts",
    title: "Connect Your Personal Social Handles",
    description: "Link your own LinkedIn and X profiles so you can share and engage personally alongside your brand.",
    target: "#onboarding-personal-accounts-accordion",
    route: "/admin/settings",
    action: "expand-personal-accounts",
    placement: "right",
  },
  {
    id: "trendingTopics",
    title: "Set Organisation Trending Topics",
    description: "Add organisation-wide trending topics to keep your team's content aligned with your brand focus areas.",
    target: "#onboarding-trending-topics-accordion",
    route: "/admin/settings",
    action: "expand-trending-topics",
    placement: "right",
  },
  {
    id: "personalTrendingTopics",
    title: "Set Your Personal Trending Topics",
    description: "Add your own personal preferred topics to get content recommendations tailored to your individual focus and thought leadership areas.",
    target: "#onboarding-admin-personal-trending-accordion",
    route: "/admin/settings",
    action: "expand-admin-personal-trending-topics",
    placement: "right",
  },
  {
    id: "createTeamContent",
    title: "Create Content for Your Team",
    description: "Use the Compose area to write posts, use AI generation, and publish content your team can share and amplify.",
    target: "#onboarding-compose-input",
    route: "/admin/timeline",
    placement: "bottom",
  },
  {
    id: "shareContent",
    title: "Share Some Content",
    description: "Lead by example — share posts to your connected networks to kick off engagement across your organisation.",
    target: "#onboarding-share-post-btn",
    route: "/admin/timeline",
    placement: "bottom",
  },
  {
    id: "buildPersonalBrand",
    title: "Build Your Personal Brand",
    description: "Consistently create and share content from the Compose page to establish your thought leadership and grow your personal brand.",
    target: "#onboarding-compose-page-input",
    route: "/admin/compose",
    placement: "bottom",
  },
];

export const OnboardingProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const saveTimerRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("role") || "[]").flat().includes("ROLE_ADMIN");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const checkRole = () => {
      try {
        setIsAdmin(JSON.parse(sessionStorage.getItem("role") || "[]").flat().includes("ROLE_ADMIN"));
      } catch {
        setIsAdmin(false);
      }
    };
    checkRole();
    window.addEventListener("storage", checkRole);
    return () => window.removeEventListener("storage", checkRole);
  }, [location.pathname]);

  const steps = useMemo(() => isAdmin ? ADMIN_STEPS : EMPLOYEE_STEPS, [isAdmin]);

  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Fetch onboarding status from backend once per session
  useEffect(() => {
    const isAppRoute = location.pathname.startsWith("/employees") || location.pathname.startsWith("/admin");

    if (!isAppRoute) {
      hasFetchedRef.current = false;
      setIsOnboardingActive(false);
      return;
    }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    onboardingService.getStatus()
      .then((res) => {
        if (res?.status && res?.data) {
          const { skipped, completed, step } = res.data;
          if (!skipped && !completed) {
            const safeStep = typeof step === "number" && step < steps.length ? step : 0;
            setActiveStep(safeStep);
            setIsOnboardingActive(true);
          }
        }
      })
      .catch((err) => {
        console.error("Onboarding status fetch failed:", err?.message);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Navigate to the current step's route whenever the active step changes
  useEffect(() => {
    if (!isOnboardingActive || steps.length === 0) return;
    const currentStep = steps[activeStep];
    if (currentStep?.route && location.pathname !== currentStep.route) {
      const timer = setTimeout(() => navigate(currentStep.route), 100);
      return () => clearTimeout(timer);
    }
  }, [activeStep, steps, isOnboardingActive, navigate, location.pathname]);

  // Fire accordion-expand action after navigation (or immediately if already on same route)
  useEffect(() => {
    if (!isOnboardingActive || steps.length === 0) return;
    const currentStep = steps[activeStep];
    if (!currentStep?.action) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("onboarding-action", { detail: { action: currentStep.action } }));
    }, 450);
    return () => clearTimeout(timer);
  }, [activeStep, steps, isOnboardingActive]);

  // Debounced API save
  const saveStatus = useCallback((payload) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      onboardingService.updateStatus(payload).catch((err) => {
        console.error("Failed to save onboarding status:", err?.message);
      });
    }, 300);
  }, []);

  const nextStep = () => {
    if (activeStep < steps.length - 1) {
      const next = activeStep + 1;
      setActiveStep(next);
      saveStatus({ step: next });
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      const prev = activeStep - 1;
      setActiveStep(prev);
      saveStatus({ step: prev });
    }
  };

  const skipTour = () => {
    setIsOnboardingActive(false);
    saveStatus({ skipped: true });
  };

  const completeTour = () => {
    setIsOnboardingActive(false);
    saveStatus({ completed: true });
  };

  const restartTour = () => {
    onboardingService.updateStatus({ step: 0, completed: false, skipped: false }).catch((err) => {
      console.error("Failed to restart onboarding:", err?.message);
    });
    setActiveStep(0);
    setIsOnboardingActive(true);
    const step0Route = steps[0]?.route;
    if (step0Route && location.pathname !== step0Route) {
      navigate(step0Route);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{ steps, activeStep, isOnboardingActive, nextStep, prevStep, skipTour, completeTour, restartTour, isAdmin }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) throw new Error("useOnboarding must be used within an OnboardingProvider");
  return context;
};
